import {ADDRESS_QUERY_KEY, PLACE_KEY} from '../Constants/StorageKeys';
import {getGoogleAutoSuggestion, getPlaceServices} from '../API/GoogleApi';
import {IAddress} from '../Types/IAddress';
import {IMapsPlacesService} from '../Types/IMapAPI';
import {ICachedPlace, IPlace} from '../Types/IPlace';
import {formatDate, isDateExpired} from '../Utils/DateUtil';

const addressStorage: Storage = sessionStorage;
const placeStorage: Storage = localStorage;

const searchAddressFromCache = (query: string): IAddress[] | null => {
    const result: string | null = addressStorage.getItem(`${ADDRESS_QUERY_KEY}${query}`);

    if (result === null) {
        return result;
    }

    try {
        return JSON.parse(result);
    } catch {
        return null;
    }
}

const searchAddressAndSaveToCache = (query: string, resolve: (value: IAddress[]) => void): void => {
    getGoogleAutoSuggestion(query).then((result: IAddress[]) => {
        addressStorage.setItem(`${ADDRESS_QUERY_KEY}_${query}`, JSON.stringify(result));
        resolve(result);
    }).catch(() => resolve([]));
}

export const searchAddress = (query: string): Promise<IAddress[]> => {
    return new Promise<IAddress[]>((resolve): void => {
       try {
           const addressFromCache: IAddress[] | null = searchAddressFromCache(query);

           if (addressFromCache !== null) {
               return resolve(addressFromCache);
           }
           searchAddressAndSaveToCache(query, resolve);
       } catch {
           searchAddressAndSaveToCache(query, resolve);
       }
    });
}

const searchPlaceDetailsAndSave = (address: IAddress, resolve: (value: IPlace | null) => void): void => {
    const placesService: IMapsPlacesService  | null = getPlaceServices();
    if (placesService !== null) {
        placesService.getDetails(
            {
                placeId: address.place_id,
                fields: [
                    'geometry',
                    'type'
                ]
            },
            (placeResult, placeResultStatus) => {
                if (placeResultStatus === google.maps.places.PlacesServiceStatus.OK) {
                    const result: IPlace = {
                        id: address.place_id.toString(),
                        latitude: typeof placeResult.geometry !== 'undefined' ? placeResult.geometry.location.lat() : null,
                        longitude: typeof placeResult.geometry !== 'undefined' ? placeResult.geometry.location.lng() : null,
                        isAirport: typeof placeResult.types !== 'undefined' ? placeResult.types.includes('airport') : false,
                        address: address,
                        name: address.description
                    }

                    const cacheItem = {
                        created: formatDate(new Date(), 'yyyy-MM-dd'),
                        result
                    }

                    placeStorage.setItem(`${PLACE_KEY}${address.place_id.toString()}`, JSON.stringify(cacheItem));
                    resolve(result)
                } else {
                    resolve(null);
                }
            }
        );
    }
}

const getPlaceFromStorage = (placeId: string): IPlace | null => {
    const result: string | null = placeStorage.getItem(`${PLACE_KEY}${placeId}`);

    if (result === null) {
        return result;
    }

    try {
        const cachedItem: ICachedPlace = JSON.parse(result) as ICachedPlace;
        const cacheIsExpired: boolean = isDateExpired(new Date(cachedItem.created));

        if (cacheIsExpired) {
            placeStorage.removeItem(`${PLACE_KEY}${placeId}`);
            return null;
        } else {
            return cachedItem.result;
        }
    } catch {
        return null;
    }
}

export const getPlaceDetails = (address: IAddress): Promise<IPlace | null> => {
   return new Promise<IPlace | null>((resolve) => {
       const placeFromCache: IPlace | null = getPlaceFromStorage(address.place_id);
       if (placeFromCache !== null) {
           resolve(placeFromCache);
       }

       searchPlaceDetailsAndSave(address, resolve);
   })
}
